import React, {useState} from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {useApplicationContext} from '../ApplicationContext';

/** Wie der Dialog geschlossen wurde — der Aufrufer entscheidet damit über das Wiedervorlegen. */
export type AdConversionCloseReason = 'converted' | 'later' | 'never' | 'cancelled';

interface AdConversionDialogProps {
    open: boolean;
    onClose: (reason: AdConversionCloseReason) => void;

    /**
     * Zeigt „Später" und „Nicht mehr fragen" statt „Abbrechen" — für die ungefragte
     * Nachfrage nach dem Login. Von der Sicherheitsseite aus reicht „Abbrechen".
     */
    asPrompt?: boolean;
}

/**
 * Stellt das eigene Konto auf AD-Anmeldung um. Der Besitznachweis ist das AD-Passwort; die
 * Rolle verschlechtert sich dabei nie — der Server lehnt sonst mit Klartext ab, der hier
 * unverändert angezeigt wird.
 */
export const AdConversionDialog: React.FC<AdConversionDialogProps> = ({open, onClose, asPrompt}) => {
    const {api} = useApplicationContext();
    const {t} = useTranslation();

    const [password, setPassword] = useState('');
    const [converting, setConverting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const reset = () => {
        setPassword('');
        setError(null);
        setSuccessMessage(null);
    };

    const close = (reason: AdConversionCloseReason) => {
        reset();
        onClose(reason);
    };

    const convert = async (): Promise<void> => {
        setConverting(true);
        setError(null);
        try {
            const response = await api.user.userConvertToAd({password});
            setSuccessMessage(response.data.message ?? t('adLogin.dialog.successFallback'));
            setPassword('');
        } catch (e: any) {
            // Der Server begründet jede Ablehnung mit Klartext (400) — anzeigen, nicht übersetzen.
            const detail = e?.response?.data;
            setError(typeof detail === 'string' && detail.length > 0
                ? detail
                : t('adLogin.dialog.errorFallback'));
        } finally {
            setConverting(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => close(successMessage ? 'converted' : 'cancelled')}
                maxWidth="sm" fullWidth>
            <DialogTitle>{t('adLogin.dialog.title')}</DialogTitle>
            <DialogContent>
                {successMessage ? (
                    <Alert severity="success">
                        <AlertTitle>{t('adLogin.dialog.successTitle')}</AlertTitle>
                        {successMessage}
                    </Alert>
                ) : (
                    <>
                        <Typography variant="body2" sx={{mb: 1}}>
                            {t('adLogin.dialog.intro')}
                        </Typography>
                        <List dense sx={{py: 0, mb: 1}}>
                            <ListItem sx={{py: 0.25}}>
                                <ListItemText primary={t('adLogin.dialog.pointPassword')}/>
                            </ListItem>
                            <ListItem sx={{py: 0.25}}>
                                <ListItemText primary={t('adLogin.dialog.pointRole')}/>
                            </ListItem>
                            <ListItem sx={{py: 0.25}}>
                                <ListItemText primary={t('adLogin.dialog.pointMfa')}/>
                            </ListItem>
                        </List>

                        <TextField
                            fullWidth size="small" type="password" autoFocus
                            label={t('adLogin.dialog.passwordLabel')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && password && !converting) void convert();
                            }}
                            helperText={t('adLogin.dialog.passwordHelp')}
                            sx={{mt: 1}}
                        />

                        {error && (
                            <Alert severity="error" sx={{mt: 2}}>{error}</Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {successMessage ? (
                    <Button variant="contained" onClick={() => close('converted')}>
                        {t('adLogin.dialog.done')}
                    </Button>
                ) : (
                    <>
                        {asPrompt ? (
                            <>
                                <Button onClick={() => close('never')} color="inherit">
                                    {t('adLogin.dialog.dontAsk')}
                                </Button>
                                <Button onClick={() => close('later')} color="inherit">
                                    {t('adLogin.dialog.later')}
                                </Button>
                            </>
                        ) : (
                            <Button onClick={() => close('cancelled')} color="inherit">
                                {t('adLogin.dialog.cancel')}
                            </Button>
                        )}
                        <Button variant="contained" onClick={convert}
                                disabled={converting || password.length === 0}>
                            {converting ? t('adLogin.dialog.converting') : t('adLogin.dialog.confirm')}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AdConversionDialog;
